import React, { useState, useEffect } from "react";
import { Col, Card, CardBody, Form, Input, Button } from "reactstrap";
import { Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import CustomModal from "../../../components/Modal/modal";
import { useDispatch, useSelector } from "react-redux";
import SDK from "../../../config";
import {
  fetchBetaTimeRequest,
  uploadBetaEmailRequest,
  searchiOSBetaRequest
} from "../../../store/iOS/iOSBetaEmails/betaEmailAction";
import PageSection from '../../../components/Loader/loader';

const AllBetaEmails = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [editableRow, setEditableRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({});
  const [CsvShowModal, setCsvShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [itemsPerPage] = useState(30);
  const [fetchedEntries, setFetchedEntries] = useState([]);
  const [totalFetchedCount, setTotalFetchedCount] = useState(0);
  const [displayedItems, setDisplayedItems] = useState([]);
  const [noResultsFound, setNoResultsFound] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const dispatch = useDispatch();
  const { betaEmailData, searchResults = [], loading } = useSelector((state) => state.betaEmail);

  useEffect(() => {
    if (!betaEmailData || betaEmailData.length === 0) {
      dispatch(fetchBetaTimeRequest(1, 120));
    } else {
      setDisplayedItems(betaEmailData);
    }
  }, [dispatch, betaEmailData]);

  // Search effect
  useEffect(() => {
    const handleSearch = () => {
      if (searchTerm.length >= 3) {
        setSearchLoading(true);
        dispatch(searchiOSBetaRequest(searchTerm));
      } else {
        setDisplayedItems(betaEmailData);
        setNoResultsFound(false);
      }
    };

    const timeoutId = setTimeout(handleSearch, 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchTerm, dispatch, betaEmailData]);

  useEffect(() => {
    if (searchTerm.length >= 3) {
      setSearchLoading(false);
      if (searchResults.length > 0) {
        setDisplayedItems(searchResults);
        setNoResultsFound(false);
      } else {
        setNoResultsFound(true);
        const clearItemsTimeout = setTimeout(() => {
          setDisplayedItems([]);
        }, 500);

        return () => clearTimeout(clearItemsTimeout);
      }
    } else {
      setDisplayedItems(betaEmailData);
      setNoResultsFound(false);
    }
  }, [searchResults, searchTerm, betaEmailData]);

  useEffect(() => {
    if (currentPage % 4 === 0 && totalFetchedCount <= currentPage * itemsPerPage) {
      const nextPage = currentPage / 4 + 1;
      dispatch(fetchBetaTimeRequest(nextPage, 120));
    }
  }, [currentPage, dispatch, totalFetchedCount]);

  const totalPages = Math.ceil(displayedItems.length / itemsPerPage);
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const displayedPaginationItems = displayedItems.slice(indexOfFirstItem, indexOfFirstItem + itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const renderPageNumbers = () => {
    const pageNumbers = [];
    let startPage, endPage;

    if (totalPages <= 3) {
      startPage = 1;
      endPage = totalPages;
    } else {
      if (currentPage <= 2) {
        startPage = 1;
        endPage = 3;
      } else if (currentPage + 1 >= totalPages) {
        startPage = totalPages - 2;
        endPage = totalPages;
      } else {
        startPage = currentPage - 1;
        endPage = currentPage + 1;
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <li
          key={i}
          className={`page-item ${i === currentPage ? "active" : ""}`}
        >
          <Link to="#" className="page-link" onClick={() => paginate(i)}>
            {i}
          </Link>
        </li>
      );
    }

    return pageNumbers;
  };

  const handleAddNewEmail = () => {
    setModalData({
      email: "",
    });
    setShowModal(true);
  };

  const handleUpdate = () => {
    if (modalData.email) {
      dispatch(uploadBetaEmailRequest(modalData.email));
      setDisplayedItems([...displayedItems, { email: modalData.email }]);
      toast.success("Email successfully added!");  
      setShowModal(false);
    } else {
      console.log("Email not provided");
    }
  };

  const handleUploadCsv = () => {
    setCsvShowModal(true);
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleCSVUpdate = async () => {
    if (!selectedFile) {
      console.log('File not selected');
      return;
    }
    
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await fetch(SDK.SOCKET_URL + "/api/iOS/beta/subscriber/csv", {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        dispatch(fetchBetaTimeRequest());  // Re-fetch data after CSV upload
        toast.success("CSV successfully uploaded!");  // Show success toast
        setCsvShowModal(false);
      } else {
        throw new Error("Failed to upload file.");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Error uploading CSV.");  // Show error toast
    }                                               
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCsvShowModal(false);
  };

  return (
    <React.Fragment>
      <Toaster />  {/* Add ToastContainer */}
      <Col lg={12}>
        <Card style={{ paddingBottom: "55px" }}>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="card-title mt-1">iOS Beta Emails</h4>
              </div>
              <div className="d-flex align-items-center">
                <Form className="app-search d-none d-lg-block pt-2" onSubmit={(e) => e.preventDefault()}>
                  <div className="position-relative d-flex align-items-center">
                    <Input
                      type="text"
                      className="form-control"
                      style={{ width: "50%" }}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search"
                    />
                    <span className="ri-search-line"></span>

                    <Button
                      style={{ fontSize: "13px", padding: "8px 9px", background: "#636678", border:'none' }}
                      color="primary"
                      className="ms-2"
                      onClick={handleAddNewEmail}
                    >
                      Add Emails
                    </Button>
                    <Button
                      style={{ fontSize: "13px", padding: "7px 9px" }}
                      color="primary"
                      className="ms-2"
                      onClick={handleUploadCsv}
                    >
                      Upload CSV
                    </Button>
                  </div>
                </Form>
              </div>
            </div>

            {loading || searchLoading ? (
              <div className="table-responsive p-2" style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50%" }}>
                <PageSection />
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-bordered" style={{ minWidth: "max-content" }}>
                  <thead>
                    <tr>
                      <th className="p-3">No.</th>
                      <th className="p-3">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedPaginationItems.length === 0 ? (
                      <tr>
                        <td colSpan="12" className="text-center p-5">
                          {noResultsFound ? (
                            <div>No data found</div>
                          ) : (
                            <PageSection />
                          )}
                        </td>
                      </tr>
                    ) : (
                      displayedPaginationItems.map((row, index) => (
                        <tr key={row.id} style={{ backgroundColor: editableRow === row.id ? "#f5f5f5" : "white" }}>
                          <td className="p-3">{indexOfFirstItem + index + 1}</td>
                          <td className="p-3">{row.email}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                <nav>
                  <ul className="pagination justify-content-end" style={{ position: "absolute", marginTop: "15px", right: "20px" }}>
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <Link to="#" className="page-link" onClick={() => paginate(currentPage - 1)}>
                        &laquo;
                      </Link>
                    </li>
                    {renderPageNumbers()}
                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                      <Link to="#" className="page-link" onClick={() => paginate(currentPage + 1)}>
                        &raquo;
                      </Link>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </CardBody>
        </Card>
      </Col>

      {/* Add New Email Modal */}
      {showModal && (
        <CustomModal
          show={showModal}
          handleClose={handleCloseModal}
          modalTitle="Add New Email"
          inputFields={[{
            label: "Email",
            type: "email",
            name: "email",
            placeholder: "Enter Your Email",
            onChange: (e) => setModalData({ email: e.target.value }),
          }]}
          updateButton={{
            variant: "primary",
            text: "Add Email",
            onClick: handleUpdate,
          }}
        />
      )}

      {/* Upload CSV Modal */}
      {CsvShowModal && (
        <CustomModal
          show={CsvShowModal}
          handleClose={handleCloseModal}
          modalTitle="Upload CSV"
          inputFields={[{
            label: "Upload CSV",
            type: "file",
            name: "file",
            placeholder: "Choose file",
            onChange: handleFileChange,
            accept: ".csv",
          }]}
          updateButton={{
            variant: "primary",
            text: "Upload",
            onClick: handleCSVUpdate,
          }}
        />
      )}
    </React.Fragment>
  );
};

export default AllBetaEmails;
